/**
 * i18next bootstrap.
 *
 * Design notes:
 *  - en-US is the source-of-truth resource and `fallbackLng`, so any key a
 *    translator hasn't filled in still renders (in English) instead of
 *    showing the raw key string.
 *  - Language is resolved in this order: explicit user preference in
 *    localStorage ("lenavio.locale") → browser `navigator.language` →
 *    `fallbackLng`. The LocaleContext is what actually writes the
 *    preference back to localStorage; i18next just reads it on boot.
 *  - No Suspense: resources are bundled inline (not fetched) so the UI
 *    never has to wait on a network round-trip for translations.
 *  - `returnNull: false` + `nsSeparator: false` keeps the `t()` call sites
 *    simple: one namespace, dotted keys.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enUS from "./locales/en-US";
import ptBR from "./locales/pt-BR";
import es from "./locales/es";
import fr from "./locales/fr";
import de from "./locales/de";

export const SUPPORTED_LOCALES = [
  { code: "en-US", label: "English (US)" },
  { code: "pt-BR", label: "Português (Brasil)" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]["code"];

export const LOCALE_STORAGE_KEY = "lenavio.locale";
/** Legacy key from the PeopleOS-era build — one-shot migrated on boot. */
const LEGACY_LOCALE_STORAGE_KEY = "peopleos.locale";

// One-shot migration: copy the old ``peopleos.*`` preferences into the new
// ``lenavio.*`` slots, then delete the originals so we don't keep two
// sources of truth. Runs synchronously before i18next reads its detector,
// which is why it lives here rather than inside React state.
function migrateLegacyStorage() {
  if (typeof window === "undefined") return;
  try {
    const moves: Array<[string, string]> = [
      [LEGACY_LOCALE_STORAGE_KEY, LOCALE_STORAGE_KEY],
      ["peopleos.timezone", "lenavio.timezone"],
      ["peopleos.currency", "lenavio.currency"],
    ];
    for (const [oldKey, newKey] of moves) {
      const legacy = window.localStorage.getItem(oldKey);
      if (legacy != null && window.localStorage.getItem(newKey) == null) {
        window.localStorage.setItem(newKey, legacy);
      }
      if (legacy != null) {
        window.localStorage.removeItem(oldKey);
      }
    }
  } catch {
    // Private-mode Safari / quota exceeded — not fatal. The user just
    // falls back to defaults and picks a language in settings again.
  }
}

migrateLegacyStorage();

// Type shape is driven by en-US; stubs use DeepPartial and fall back.
export type Resources = { translation: typeof enUS };

/**
 * Resolve the initial language from localStorage ourselves. We used to
 * wire in ``i18next-browser-languagedetector`` for this, but the detector
 * was caching normalized codes (e.g. ``en`` instead of ``en-US``) which
 * then failed to match our case-sensitive resource keys, so ``t()``
 * returned raw keys for English. Resolving here — synchronously, against
 * the exact ``SUPPORTED_LOCALES`` codes — keeps the mapping unambiguous.
 */
function resolveInitialLanguage(): string {
  if (typeof window === "undefined") return "en-US";
  const codes = SUPPORTED_LOCALES.map((l) => l.code) as readonly string[];
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!saved) return "en-US";
    if (codes.includes(saved)) return saved;
    // Normalize legacy/cached values written by the old language
    // detector (``en``, ``en-us``, ``i18next``'s lowercased variants)
    // back onto our canonical codes, then rewrite storage so this branch
    // only runs once.
    const prefix = saved.split("-")[0].toLowerCase();
    const match = codes.find((c) => c.toLowerCase().startsWith(prefix));
    const resolved = match ?? "en-US";
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, resolved);
    } catch {
      /* ignore quota / private mode */
    }
    return resolved;
  } catch {
    return "en-US";
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    "en-US": { translation: enUS },
    "pt-BR": { translation: ptBR },
    es: { translation: es },
    fr: { translation: fr },
    de: { translation: de },
  },
  lng: resolveInitialLanguage(),
  fallbackLng: "en-US",
  supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
  // NB: do NOT turn on ``nonExplicitSupportedLngs``. In i18next 23.x it
  // strips ``en-US`` down to ``en`` before checking ``supportedLngs``,
  // and since our list has ``en-US`` / ``pt-BR`` (not the bare prefixes)
  // every lookup for those locales silently falls through to an empty
  // resolve chain and ``t()`` returns the raw key. We resolve prefixes
  // ourselves in ``resolveInitialLanguage`` and ``normalizeLocale``, so
  // we don't need i18next's built-in prefix matching.
  load: "currentOnly",
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnNull: false,
  react: {
    useSuspense: false,
  },
});

export default i18n;
